import {
  useState,
  useCallback,
  useRef,
  forwardRef,
  useEffect,
  useContext,
  useImperativeHandle,
} from 'react';
import { observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { useIntl, FormattedMessage } from 'react-intl';
import { asEntity } from '~/Hoc';
import StatusField from '../../../../../../Components/From/StatusField';
import useStyles from './styles';
import { UserContext } from '~/Services/Providers';
import Loading from '../../../../../../Components/Loading';
import {
  baseGAObject,
  getGAOriginalDate,
  getGAEventObjectOnBlurFieldError,
  getGAEventObjectOnFirstInteract,
} from '~/Services/Helpers';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import {
  ActionButton,
  PhoneNumberInput,
  Input,
} from '../../../../../../Components/From';
import {
  RESET_METHODS,
  normalizeCell,
  isEmailValid,
  isPhoneValid,
  normalizeError,
} from './data';
import { Box } from '@material-ui/core';
import useResetFormNavigation from './Partials/useResetFormNavigation';

const { SMS, EMAIL } = RESET_METHODS;

const schedule = (cb) => {
  if (typeof window === 'undefined') return;
  (window.requestAnimationFrame ?? ((fn) => setTimeout(fn, 0)))(cb);
};

const gaSafeDispatch = (evt) => {
  try {
    ReactGA.event(evt);
    TagManager.dataLayer({ dataLayer: evt });
  } catch {}
};

const UnifiedResetPasswordForm = forwardRef(function UnifiedResetPasswordForm(
  {
    classes,
    setLoading,
    setErrorMessage,
    moveForward,
    entityStore,
    isDisabled,
    loading,
  },
  ref
) {
  const { userContextObj, setUserContext } = useContext(UserContext);
  const intl = useIntl();

  const emailRef = useRef(null);
  const cellRef = useRef(null);
  const smsRadioRef = useRef(null);
  const emailRadioRef = useRef(null);
  const methodFocusInitializedRef = useRef(false);
  const radioGroupRef = useRef(null);
  const [method, setMethod] = useState('');
  const [email, setEmail] = useState('');
  const [cell, setCell] = useState('');
  const [methodAnnounce, setMethodAnnounce] = useState('');
  const [errors, setErrors] = useState({ email: '', cell: '' });
  const isSwitchingRef = useRef(false);
  const [cellKey, setCellKey] = useState(0);

  const smsMainId = 'sms';
  const emailMainId = 'email';
  const smsSecondId = 'ResetYourPasswordSMSText';
  const emailSecondId = 'ResetYourPasswordEmail';

  const isEmailMethod = method === EMAIL;
  const isSmsMethod = method === SMS;

  const firstInteraction = useRef(true);

  const getAnnounceTitle = useCallback(
    (method) => {
      const methodLabel = intl.formatMessage({
        id: method === SMS ? smsMainId : emailMainId,
      });
      const methodDescription = intl.formatMessage({
        id: method === SMS ? smsSecondId : emailSecondId,
      });
      return `${methodLabel}. ${methodDescription}`;
    },
    [intl]
  );

  const focusField = useCallback((field) => {
    const refMap = { email: emailRef.current, cell: cellRef.current };
    const el = refMap[field] ?? document.getElementById(field);
    if (!el) return;
    if (el.disabled) {
      schedule(() => el?.focus?.());
      return;
    }
    el.focus?.();
  }, []);

  useEffect(() => {
    schedule(() => {
      setMethod(SMS);
      focusField('cell');
    });
    setTimeout(() => setMethodAnnounce(getAnnounceTitle(SMS)), 1000);
  }, [getAnnounceTitle]);

  useEffect(() => {
    if (!methodFocusInitializedRef.current) {
      methodFocusInitializedRef.current = true;
      return;
    }
    focusField(isEmailMethod ? 'email' : 'cell');
  }, [isEmailMethod, focusField]);

  const setFieldError = useCallback((field, message) => {
    setErrors((prev) =>
      prev[field] === message ? prev : { ...prev, [field]: message || '' }
    );
  }, []);

  const clearFieldError = useCallback(
    (field) => {
      setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
      setErrorMessage?.('');
    },
    [setErrorMessage]
  );

  const trackBlurError = useCallback((field, hadValue) => {
    const errorType = hadValue ? 'error' : 'abandoned';
    const evt = getGAEventObjectOnBlurFieldError(field, errorType);
    gaSafeDispatch(evt);
  }, []);

  const validateField = useCallback(
    (field, event) => {
      const value = event?.target?.value?.trim?.();
      const trimmed = String(value ?? '').trim();
      setTimeout(() => {
        const smsRadio = document.getElementById('radio-sms');
        const emailRadio = document.getElementById('radio-email');
        if (field === 'cell' && emailRadio?.checked) {
          return true;
        }
        if (field === 'email' && smsRadio?.checked) {
          return true;
        }
        if (field === 'cell') {
          const valid = isPhoneValid(trimmed);
          if (!valid) {
            setFieldError('cell', 'resetPasswordSMSRequired');
            trackBlurError('cell', Boolean(trimmed));
            return false;
          }
          setFieldError('cell', '');
          return true;
        }
        if (field === 'email') {
          const emailErr = isEmailValid(trimmed);
          setFieldError('email', emailErr || '');
          if (emailErr) trackBlurError('email', Boolean(trimmed));
          return !emailErr;
        }
        return true;
      }, 300);
    },
    [setFieldError, trackBlurError]
  );

  const sendGAEvent = useCallback((methodValue, action = 'method_change') => {
    const bookingStage =
      localStorage.getItem('booking_stage') || 'reset_password';
    const originalFields = getGAOriginalDate(bookingStage);
    const gaClick =
      action === 'submit'
        ? `${methodValue}_submit`
        : `${methodValue}_selection`;
    const GAObject = {
      ...baseGAObject,
      event: 'button_click',
      content_id: '/wink/book',
      click: gaClick,
      territory: 'en_CA',
      booking_stage: bookingStage,
      booking_type: localStorage.getItem('ga_booking_type'),
      store_name: localStorage.getItem('store'),
      view_state: 'main',
      dpdItem: action === 'submit' ? 'wink-7a' : 'wink-36',
      ...originalFields,
    };
    gaSafeDispatch(GAObject);
  }, []);

  const sendFirstInteract = useCallback((fieldName) => {
    if (firstInteraction.current) {
      firstInteraction.current = false;
      const evt = getGAEventObjectOnFirstInteract(fieldName);
      gaSafeDispatch(evt);
    }
  }, []);

  const changeMethod = useCallback(
    (
      nextMethod,
      { track = true, shouldFocus = true, preserveValues = false } = {}
    ) => {
      setMethodAnnounce(getAnnounceTitle(nextMethod));
      if (isDisabled || loading) return;

      if (isSwitchingRef.current) return;
      isSwitchingRef.current = true;

      if (method === nextMethod) {
        if (shouldFocus) focusField(nextMethod === EMAIL ? 'email' : 'cell');

        schedule(() => {
          isSwitchingRef.current = false;
        });
        return;
      }

      setErrorMessage?.('');
      setMethod(nextMethod);

      if (!preserveValues) {
        if (nextMethod === EMAIL) {
          clearFieldError('cell');
          setCell('');
          setCellKey((k) => k + 1);
        } else {
          clearFieldError('email');
          setEmail('');
        }
      } else {
        if (nextMethod === EMAIL) clearFieldError('cell');
        else clearFieldError('email');
      }

      if (track) sendGAEvent(nextMethod);
      if (shouldFocus) focusField(nextMethod === EMAIL ? 'email' : 'cell');

      schedule(() => {
        isSwitchingRef.current = false;
      });
    },
    [getAnnounceTitle, isDisabled, loading, method, setErrorMessage, sendGAEvent, focusField, clearFieldError]
  );

  const handleMethodChange = useCallback(
    (event) => {
      const next = event?.target?.value || SMS;
      changeMethod(next, { track: true, shouldFocus: false });
    },
    [changeMethod]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setErrorMessage?.('');
      sendGAEvent(method, 'submit');

      const finalValues =
        method === EMAIL
          ? { email: email.trim(), cell: '' }
          : { email: '', cell: normalizeCell(cell) };

      if (method === EMAIL) {
        const emailValidationError = isEmailValid(finalValues.email);
        if (emailValidationError) {
          const errorMsg = normalizeError(
            emailValidationError,
            'validationError'
          );
          setFieldError('email', errorMsg);
          setErrorMessage?.('validationError');
          setLoading?.(false);
          focusField('email');
          return;
        }
      } else if (!finalValues.cell) {
        setFieldError('cell', 'resetPasswordSMSRequired');
        setErrorMessage?.('validationError');
        setLoading?.(false);
        focusField('cell');
        return;
      }

      if (method === SMS && setUserContext) {
        setUserContext({
          cell: finalValues.cell,
          resend: {
            accountsId: userContextObj?.accountId,
            email: '',
            cell: finalValues.cell,
            originLink: userContextObj?.origin,
          },
        });
      }

      entityStore.post({
        accountsId: userContextObj?.accountId,
        email: finalValues.email,
        cell: finalValues.cell,
        originLink: userContextObj?.origin,
      });
    },
    [
      method,
      email,
      cell,
      entityStore,
      userContextObj,
      setUserContext,
      setLoading,
      setErrorMessage,
      sendGAEvent,
      setFieldError,
      focusField,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      entityDidPosted() {
        if (!method) return;
        moveForward?.(method === EMAIL ? {email: true, cell: false} : {email: false, cell: true});
        setLoading?.(false);
      },
      entityDidCatch(err) {
        if (!method) return;
        const message = method === EMAIL ? 'resetEmailError' : 'resetSMSError';
        setErrorMessage?.(message);
        setLoading?.(false);
        if (method === EMAIL) {
          focusField('email')
        } else {
          focusField('cell')
        }
      },
    }),
    [method, moveForward, setLoading, setErrorMessage, setFieldError, changeMethod]
  );

  const { handleKeyNavigation } = useResetFormNavigation({
    method,
    email,
    cell,
    setEmail,
    setCell,
    clearFieldError,
    changeMethod,
    classes,
    setCellKey,
  });

  if (loading) return <Loading />;

  return (
    <>
      <StatusField>{methodAnnounce}</StatusField>
      <div className={classes.FormWrapper}>
        <form onSubmit={handleSubmit} noValidate ref={ref}>
          <RadioGroup
            aria-labelledby="choose-method"
            name="resetMethod"
            value={method}
            onChange={handleMethodChange}
            onKeyDown={handleKeyNavigation}
            innerRef={radioGroupRef}
          >
            <FormControlLabel
              classes={{
                root: classes.fullRowFormControlLabel,
                label: classes.fullRowLabelSlot,
              }}
              value={SMS}
              control={
                <Radio
                  name={`reset-method-${SMS}`}
                  id="radio-sms"
                  classes={{
                    root: classes.radioRoot,
                    checked: classes.radioChecked,
                  }}
                  color="primary"
                  inputProps={{
                    'aria-describedby': 'ResetYourPasswordSMSText',
                  }}
                  inputRef={smsRadioRef}
                />
              }
              label={
                <Box className={classes.description}>
                  <span>
                    <FormattedMessage id={smsMainId} defaultMessage="Email" />
                  </span>
                  <div id="ResetYourPasswordSMSText">
                    <FormattedMessage
                      id={smsSecondId}
                      defaultMessage="We'll text a code to your phone (message and data rates may apply)"
                    />
                  </div>
                  <div>
                    <PhoneNumberInput
                      key={`cell-${cellKey}`}
                      name="cell"
                      id="cell"
                      inputRef={cellRef}
                      type="tel"
                      value={cell}
                      InputChange={(event) => {
                        setCell(event?.target?.value || '');
                        sendFirstInteract('cell');
                      }}
                      blur={(event) => {
                        validateField('cell', event);
                      }}
                      placeholder={intl.formatMessage({
                        id: 'resetPasswordForm.cell',
                      })}
                      title={intl.formatMessage({
                        id: 'resetPasswordForm.cell',
                      })}
                      required
                      disabled={isDisabled || loading || !isSmsMethod}
                      aria-invalid={isSmsMethod && Boolean(errors.cell)}
                      aria-errormessage="cell-error"
                      error={isSmsMethod && Boolean(errors.cell)}
                      errorText={isSmsMethod ? errors.cell : ''}
                      aria-describedby={errors.cell ? 'cell-error' : ''}
                    />
                  </div>
                </Box>
              }
              sx={{ alignItems: 'flex-start', width: '100%' }}
            />
            <FormControlLabel
              classes={{
                root: classes.fullRowFormControlLabel,
                label: classes.fullRowLabelSlot,
              }}
              value={EMAIL}
              control={
                <Radio
                  classes={{
                    root: classes.radioRoot,
                    checked: classes.radioChecked,
                  }}
                  name={`reset-method-${EMAIL}`}
                  id="radio-email"
                  color="primary"
                  inputProps={{
                    'aria-describedby': 'ResetYourPasswordEmail',
                  }}
                  inputRef={emailRadioRef}
                />
              }
              label={
                <Box className={classes.description}>
                  <span>
                    <FormattedMessage id={emailMainId} defaultMessage="Email" />
                  </span>
                  <div id="ResetYourPasswordEmail">
                    <FormattedMessage
                      id={emailSecondId}
                      defaultMessage="We'll send a password reset link to your email address."
                    />
                  </div>
                  <div>
                    <Input
                      placeholder={intl.formatMessage({ id: 'email' })}
                      variant="outlined"
                      type="email"
                      name="email"
                      id="email"
                      value={email}
                      required
                      blur={(event) => validateField('email', event)}
                      disabled={isDisabled || loading || !isEmailMethod}
                      InputChange={(event) => {
                        setEmail(event?.target?.value ?? '');
                        sendFirstInteract('email');
                      }}
                      inputRef={emailRef}
                      aria-invalid={isEmailMethod && Boolean(errors.email)}
                      aria-errormessage="email-error"
                      error={isEmailMethod && Boolean(errors.email)}
                      errorText={isEmailMethod ? errors.email : ''}
                      aria-describedby={errors.email ? 'email-error' : ''}
                    />
                  </div>
                </Box>
              }
              sx={{ alignItems: 'flex-start', width: '100%' }}
            />
          </RadioGroup>

          <div className={classes.submitBtnContainer}>
            <ActionButton
              title="proceedWithPasswordReset"
              type="submit"
              variant="contained"
              loading={loading}
              disabled={loading || isDisabled}
              customClass={classes.SubmitButton}
              aria-label={intl.formatMessage({
                id: 'proceedWithPasswordReset',
              })}
            />
          </div>
        </form>
      </div>
    </>
  );
});

export default withStyles(useStyles)(
  asEntity({ storeId: 'unifiedResetPasswordForm' })(
    observer(UnifiedResetPasswordForm)
  )
);
